import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";

const todayIso = () => new Date().toISOString().slice(0, 10);
const ORDER = { Template: 0, Fabrication: 1, Install: 2, Service: 3 };

export default function CrewDaySheet() {
  const [date, setDate] = useState(todayIso());
  const [crew, setCrew] = useState("all");
  const [events, setEvents] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [jobs, setJobs] = useState({});
  const [sinks, setSinks] = useState({});

  useEffect(() => {
    api.get("/crew-list").then(({ data }) => setCrewList(data));
  }, []);

  useEffect(() => {
    api
      .get("/jobs/schedule", { params: { month: date.slice(0, 7) } })
      .then(({ data }) => setEvents(data.events));
  }, [date]);

  const stops = useMemo(() => {
    return events
      .filter((e) => e.date === date)
      .filter((e) => crew === "all" || (e.crew || "Unassigned") === crew)
      .sort((a, b) => (ORDER[a.kind] ?? 9) - (ORDER[b.kind] ?? 9));
  }, [events, date, crew]);

  useEffect(() => {
    const ids = [...new Set(stops.map((s) => s.job_id))].filter(
      (id) => !jobs[id],
    );
    if (!ids.length) return;
    Promise.all(
      ids.map((id) =>
        api
          .get(`/jobs/${id}`)
          .then((r) => r.data)
          .catch(() => null),
      ),
    ).then((list) => {
      const add = {};
      list.filter(Boolean).forEach((j) => {
        add[j.id] = j;
      });
      setJobs((prev) => ({ ...prev, ...add }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops]);

  useEffect(() => {
    const ids = [...new Set(stops.map((s) => s.job_id))].filter(Boolean);
    if (!ids.length) return;
    Promise.all(
      ids.map((id) =>
        api
          .get("/sink-allocations", {
            params: { entity: "job", entity_id: id },
          })
          .then((r) => [id, r.data.items])
          .catch(() => [id, []]),
      ),
    ).then((pairs) => setSinks(Object.fromEntries(pairs)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops]);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Jobs / Day Sheet" />
      <main className="relative z-10 mx-auto max-w-4xl px-4 pb-20 pt-6">
        <div className="no-print flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Jobs / Day Sheet
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Crew Day Sheet
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Template, fabrication, install and service stops for one day,
              ready to print
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/projects"
              data-testid="daysheet-board-link"
              className="flex h-12 items-center rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Stage Board
            </Link>
            <Button
              data-testid="print-day-sheet-button"
              onClick={() => window.print()}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
            >
              <Printer size={18} weight="bold" className="mr-2" /> Print Sheet
            </Button>
          </div>
        </div>

        <div className="no-print mt-6 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Date
            </Label>
            <Input
              data-testid="daysheet-date-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-12 border-zinc-800 bg-zinc-900"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Crew Member
            </Label>
            <Select value={crew} onValueChange={setCrew}>
              <SelectTrigger
                data-testid="daysheet-crew-select"
                className="h-12 border-zinc-800 bg-zinc-900"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="all">Whole shop</SelectItem>
                <SelectItem value="Unassigned">Unassigned</SelectItem>
                {crewList.map((u) => (
                  <SelectItem key={u.id} value={u.name || u.email}>
                    {u.name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div
          id="print-area"
          className="print-sheet mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="day-sheet"
        >
          <div className="flex items-start justify-between border-b border-zinc-700 pb-3">
            <div>
              <div className="text-lg font-black uppercase tracking-wide">
                Brioni Granite
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Crew Day Sheet
              </div>
            </div>
            <div className="text-right">
              <div
                className="font-mono-data text-sm font-bold"
                data-testid="day-sheet-date"
              >
                {date}
              </div>
              <div
                className="text-xs uppercase tracking-widest text-zinc-500"
                data-testid="day-sheet-crew"
              >
                {crew === "all" ? "Whole shop" : crew}
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3" data-testid="day-sheet-stops">
            {stops.length === 0 && (
              <p className="py-6 text-center text-sm text-zinc-500">
                Nothing scheduled for this day.
              </p>
            )}
            {stops.map((s, i) => {
              const job = jobs[s.job_id];
              const notes = job?.notes || [];
              return (
                <div
                  key={`${s.job_id}-${s.kind}`}
                  data-testid={`day-sheet-stop-${s.job_id}-${s.kind}`}
                  className="rounded-md border border-zinc-800 p-4"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono-data text-xs text-zinc-500">
                      {i + 1}.
                    </span>
                    <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest">
                      {s.kind} · {s.time || s.window}
                    </span>
                    {s.areas?.length > 0 && (
                      <span
                        className="rounded-full border border-indigo-500/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-indigo-200"
                        data-testid={`day-sheet-areas-${s.job_id}-${s.kind}`}
                      >
                        {s.areas.join(", ")}
                      </span>
                    )}
                    <span className="font-mono-data text-xs text-yellow-500">
                      {s.code}
                    </span>
                    <span className="ml-auto text-xs text-zinc-500">
                      {s.crew || "Unassigned"}
                    </span>
                  </div>
                  <div className="mt-2 text-xl font-bold">{s.project_name}</div>
                  <div className="text-base text-zinc-300">
                    {s.customer_name || "—"}
                    {s.customer_phone ? ` · ${s.customer_phone}` : ""}
                  </div>
                  {job?.address && (
                    <div className="mt-1 inline-flex items-center gap-1 text-base font-medium">
                      <MapPin size={16} />
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Job Address
                      </span>{" "}
                      {job.address}
                    </div>
                  )}
                  {job?.summary && (
                    <div className="mt-2 text-sm">
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Scope{" "}
                      </span>
                      {job.summary}
                    </div>
                  )}
                  {(sinks[s.job_id] || []).length > 0 && (
                    <div
                      className="mt-1 text-sm"
                      data-testid={`day-sheet-sinks-${s.job_id}`}
                    >
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Sink from stock{" "}
                      </span>
                      {sinks[s.job_id]
                        .map(
                          (a) =>
                            `${[a.brand, a.model].filter(Boolean).join(" ")}${
                              a.qty > 1 ? ` ×${a.qty}` : ""
                            } (${a.code}${a.location ? ` · shelf ${a.location}` : ""})`,
                        )
                        .join(", ")}
                    </div>
                  )}
                  {(job?.sink_location || job?.sink_notes) && (
                    <div className="mt-1 text-sm">
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Sink{" "}
                      </span>
                      {job.sink_location || "—"}
                      {job.sink_notes ? ` · ${job.sink_notes}` : ""}
                    </div>
                  )}
                  {(job?.material_links || []).length > 0 && (
                    <div className="mt-1 text-sm">
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Material{" "}
                      </span>
                      {job.material_links
                        .map((l) => `${l.code} ${l.label}`)
                        .join(", ")}
                    </div>
                  )}
                  {notes.length > 0 && (
                    <div className="mt-2 text-sm">
                      <span className="text-xs uppercase tracking-widest text-zinc-500">
                        Latest note{" "}
                      </span>
                      {notes[notes.length - 1].body}
                    </div>
                  )}
                  <div className="mt-3 flex gap-6 text-xs text-zinc-500">
                    <span>Arrived ______</span>
                    <span>Finished ______</span>
                    <span>Signature ____________</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
